import React from "react";
import {
  Button,
  IconButton,
  MenuItem,
  Typography,
  Tooltip,
  Box,
  List,
  SxProps,
  Theme,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Form,
  MenuButton,
  TextControl,
  TruncateTooltip,
} from "../../../components";
import {
  CircleCheckOutline,
  CloseBig,
  DownloadOutline,
  Edit,
  ErrorOutline,
  WarningOutline,
} from "@tiny/icons";
import useWorkflowsHeader from "./WorkflowsHeader.hooks";
import QuitWorkflows from "./QuitWorkflows";
import WorkflowRestrictions from "../../../customComponents/WorkflowRestrictions";
import FeedbackButton from "../../../customComponents/FeedbackButton";
import FeatureNotAvailableDialog from "../../../customComponents/FeatureNotAvailableDialog";

const WorkflowsHeader = (): JSX.Element => {
  const {
    downloadEnabled,
    isDownloading,
    editTitle,
    form,
    formTitle,
    headerControlAction,
    onClickDownload,
    quitWorkflows,
    saveTitle,
    setFormTitle,
    titleMaxLength,
    toggleEditTitle,
    workflowTitle,
    restrictions,
    viewMode,
    showFeatureNotAvailable,
    closeFeatureNotAvailableDialog,
    isWelcomeSeries,
    allowReentry,
    onToggleReentry,
  } = useWorkflowsHeader();
  return (
    <Box
      sx={{
        height: "100%",
      }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          height: 72,
          backgroundColor: "white",
          border: `1px solid ${theme.palette.grey[60]}`,
        })}
      >
        <Box
          sx={(theme) => ({
            boxSizing: "border-box",
            flex: "1",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            paddingRight: 4,
            [theme.breakpoints.up("md")]: {
              paddingRight: 40,
            },
            [theme.breakpoints.up("lg")]: {
              paddingRight: 80,
            },
            gap: 1.5,
          })}
        >
          <Box
            sx={() => ({
              flex: "0 0 54px",
              boxSizing: "border-box",
              padding: "0 12px 0 20px",
            })}
          >
            <IconButton size="small" onClick={quitWorkflows}>
              <CloseBig />
            </IconButton>
          </Box>
          <Box
            sx={{
              minWidth: "10px",
            }}
          >
            {editTitle ? (
              <Form
                form={form}
                onSubmit={saveTitle}
                style={{
                  width: "100%",
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <TextControl
                      name="name"
                      onKeyDown={() => {}}
                      autoFocus
                      label="Edit Title"
                      defaultValue={workflowTitle}
                      maxLength={titleMaxLength}
                      value={formTitle}
                      onChange={(e) => setFormTitle(e.target.value)}
                    />
                  </Grid>
                  <Grid item>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={!formTitle}
                    >
                      Save
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button variant="outlined" onClick={toggleEditTitle}>
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            ) : (
              <TruncateTooltip arrow>
                <Typography
                  variant="h4"
                  component="h1"
                  fontWeight="bold"
                  color="grey.100"
                  noWrap
                  m={0}
                >
                  {workflowTitle}
                </Typography>
              </TruncateTooltip>
            )}
          </Box>
          {!editTitle && !viewMode && !isWelcomeSeries && (
            <IconButton size="small" onClick={toggleEditTitle}>
              <Edit />
            </IconButton>
          )}
        </Box>
        <Box
          sx={(theme) => ({
            boxSizing: "border-box",
            flex: "0 0 auto",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            padding: "0 12px",
            [theme.breakpoints.up("md")]: {
              gap: 3,
              padding: "0 20px",
            },
            [theme.breakpoints.up("lg")]: {
              gap: 5,
              padding: "0 20px 0 60px",
            },
          })}
        >
          <FeedbackButton />
          {downloadEnabled && (
            <Tooltip
              title={<Typography sx={{ p: 2 }}>Download Reports</Typography>}
              sx={{ px: 2 }}
            >
              <Button
                variant="text"
                endIcon={<DownloadOutline />}
                onClick={onClickDownload}
                disabled={isDownloading}
                sx={(theme) => ({
                  [theme.breakpoints.down("md")]: {
                    "& .MuiButton-endIcon": { margin: 0 },
                    minWidth: "auto",
                    px: 1,
                    "& span:first-of-type": { display: "none" },
                  },
                })}
              >
                Download Stats
              </Button>
            </Tooltip>
          )}
          <Tooltip
            title={
              <Typography sx={{ p: 2 }}>
                When enabled, a contact that re-qualifies for the trigger will
                re-enter this workflow from the beginning.
              </Typography>
            }
          >
            <FormControlLabel
              sx={{ m: 0, whiteSpace: "nowrap" }}
              control={
                <Switch
                  checked={allowReentry}
                  onChange={(e) => onToggleReentry(e.target.checked)}
                  disabled={viewMode}
                />
              }
              label="Allow Re-entry"
            />
          </Tooltip>
          <WorkflowRestrictions restrictions={restrictions} />
          {headerControlAction.children ? (
            <MenuButton
              disabled={headerControlAction.disabled}
              color="primary"
              text={headerControlAction.text}
              variant="contained"
            >
              {headerControlAction.children
                ?.filter((a) => !a.hidden)
                ?.map((action) => (
                  <MenuItem
                    key={action.text}
                    onClick={action.onClick}
                    disabled={action.disabled}
                  >
                    {action.text}
                  </MenuItem>
                ))}
            </MenuButton>
          ) : (
            <Button
              variant="contained"
              disabled={headerControlAction.disabled}
              onClick={headerControlAction.onClick}
            >
              {headerControlAction.text}
            </Button>
          )}
        </Box>
      </Box>
      <QuitWorkflows />
      <FeatureNotAvailableDialog
        open={showFeatureNotAvailable}
        onClose={closeFeatureNotAvailableDialog}
      />
    </Box>
  );
};

export default WorkflowsHeader;
